if (!customElements.get('payment-installment')) {
  customElements.define(
    'payment-installment',
    class PaymentInstallment extends HTMLElement {
      constructor() {
        super();
        this.amountEl = this.querySelector('[data-installment-amount]');
        this.months = parseInt(this.dataset.months, 10) || 3;
        this.interest = parseFloat(this.dataset.interest) || 0;
        this.scheduled = false;

        try {
          this.variantPrices = JSON.parse(this.querySelector('[data-variant-prices]').textContent);
        } catch (e) {
          this.variantPrices = {};
        }

        this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'PHP',
        });
      }

      connectedCallback() {
        if (!this.amountEl) return;

        this.scope = this.closest('product-info') || document;
        this.scheduleUpdate = this.scheduleUpdate.bind(this);

        // Kaching Bundles changes the selected deal on click/change and re-renders its own markup.
        this.scope.addEventListener('click', this.scheduleUpdate);
        this.scope.addEventListener('change', this.scheduleUpdate);
        this.observer = new MutationObserver(this.scheduleUpdate);
        this.observer.observe(this.scope, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['class', 'checked', 'value', 'aria-checked', 'data-selected'],
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data.variant;
            if (variant) this.variantPrices[variant.id] = variant.price;
            this.scheduleUpdate();
          });
        }

        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.scope?.removeEventListener('click', this.scheduleUpdate);
        this.scope?.removeEventListener('change', this.scheduleUpdate);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      scheduleUpdate() {
        if (this.scheduled) return;
        this.scheduled = true;
        requestAnimationFrame(() => {
          this.scheduled = false;
          this.update();
        });
      }

      update() {
        const total = this.getBundlePrice() ?? this.getFormPrice();
        if (total == null) return;

        const monthly = (total * (1 + (this.interest / 100) * this.months)) / this.months;
        const text = this.formatter.format(monthly);
        if (this.amountEl.textContent !== text) this.amountEl.textContent = text;
      }

      // Price (in major units) of the selected Kaching Bundles deal, or null when no bundle is shown.
      getBundlePrice() {
        const bundle = this.scope.querySelector('kaching-bundles-block, .kaching-bundles');
        if (!bundle) return null;

        const checked = bundle.querySelector('input[type="radio"]:checked');
        const selected =
          bundle.querySelector('.kaching-bundles__bar--selected') ||
          checked?.closest('.kaching-bundles__bar, label') ||
          bundle.querySelector('[aria-checked="true"], [data-selected="true"]');
        if (!selected) return null;

        let priceEl = selected.querySelector('.kaching-bundles__bar-price');
        if (!priceEl) {
          priceEl = Array.from(selected.querySelectorAll('[class*="price"]')).find(
            (el) => !/full|compare|original|saving|per-item|unit/i.test(el.className) && /\d/.test(el.textContent)
          );
        }

        return priceEl ? this.parsePrice(priceEl.textContent) : null;
      }

      // Fallback: selected variant price × quantity from the product form.
      getFormPrice() {
        const form = document.getElementById(this.dataset.productFormId);
        const variantId = form?.querySelector('[name="id"]')?.value;
        const cents = this.variantPrices[variantId];
        if (cents == null) return null;

        const quantityInput = this.scope.querySelector(`[name="quantity"][form="${this.dataset.productFormId}"]`);
        const quantity = parseInt(quantityInput?.value, 10) || 1;
        return (cents / 100) * quantity;
      }

      parsePrice(text) {
        let value = text.replace(/[^\d.,]/g, '');
        if (!value) return null;

        // Treat a trailing ",dd" as a decimal comma (e.g. 1.299,00); otherwise commas are thousands separators.
        if (/,\d{1,2}$/.test(value)) {
          value = value.replace(/\./g, '').replace(',', '.');
        } else {
          value = value.replace(/,/g, '');
        }

        const number = parseFloat(value);
        return isNaN(number) ? null : number;
      }
    }
  );
}
